import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Plus, Search, Truck, ChevronRight, Package, User,
  Calendar, AlertTriangle, Clock
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const statusLabels = {
  available: 'Disponible',
  on_loan: 'En prêt',
  in_maintenance: 'En maintenance'
};

const statusColors = {
  available: 'bg-[#142915] text-[#00E01A] border-[#00E01A]/20',
  on_loan: 'bg-[#2A2010] text-[#F39C12] border-[#F39C12]/20',
  in_maintenance: 'bg-[#2A1515] text-[#E74C3C] border-[#E74C3C]/20'
};

const LoanForm = ({ products, franchises, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    product_id: '',
    serial_number: '',
    franchise_id: franchises[0]?.id || '',
    status: 'available'
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Produit *</Label>
          <Select
            value={formData.product_id}
            onValueChange={(value) => setFormData({ ...formData, product_id: value })}
          >
            <SelectTrigger data-testid="loan-product-select">
              <SelectValue placeholder="Sélectionner..." />
            </SelectTrigger>
            <SelectContent className="max-h-[200px]">
              {products.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name} ({p.reference})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>N° de série *</Label>
          <Input
            value={formData.serial_number}
            onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
            placeholder="Ex: LOAN-001"
            className="font-mono"
            required
            data-testid="loan-serial-input"
          />
        </div>
        <div className="space-y-2">
          <Label>Franchise *</Label>
          <Select
            value={formData.franchise_id}
            onValueChange={(value) => setFormData({ ...formData, franchise_id: value })}
          >
            <SelectTrigger data-testid="loan-franchise-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {franchises.map((f) => (
                <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t border-[#2C3338]">
        <Button type="button" variant="outline" onClick={onClose}>
          Annuler
        </Button>
        <Button type="submit" className="bg-[#00E01A] hover:bg-[#00B815] text-black">
          Créer
        </Button>
      </div>
    </form>
  );
};

const InlineClientForm = ({ onCreated, onCancel, api }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.first_name || !formData.last_name) {
      toast.error('Prénom et nom requis');
      return;
    }
    setSaving(true);
    try {
      const res = await api.get('/franchises');
      const franchiseId = res.data[0]?.id;
      if (!franchiseId) {
        toast.error('Aucune franchise disponible');
        return;
      }
      const clientRes = await api.post('/clients', {
        ...formData,
        franchise_id: franchiseId,
        status: 'active'
      });
      toast.success('Client créé');
      onCreated(clientRes.data);
    } catch (error) {
      console.error('Failed to create client:', error);
      toast.error('Erreur lors de la création du client');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-[#141617] rounded-lg p-3 space-y-3 border border-[#00E01A]/30">
      <div className="flex items-center justify-between">
        <p className="text-[#00E01A] text-sm font-semibold">Nouveau client</p>
        <button type="button" onClick={onCancel} className="text-[#8C8F94] hover:text-white text-xs">
          Annuler
        </button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Input
          value={formData.first_name}
          onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
          placeholder="Prénom *"
          className="text-sm"
          data-testid="inline-client-first-name"
        />
        <Input
          value={formData.last_name}
          onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
          placeholder="Nom *"
          className="text-sm"
          data-testid="inline-client-last-name"
        />
        <Input
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          placeholder="Email"
          type="email"
          className="text-sm"
          data-testid="inline-client-email"
        />
        <Input
          value={formData.phone}
          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          placeholder="Téléphone"
          className="text-sm"
          data-testid="inline-client-phone"
        />
      </div>
      <Button
        type="button"
        size="sm"
        onClick={handleSubmit}
        disabled={saving}
        className="w-full bg-[#00E01A] hover:bg-[#00B815] text-black text-sm"
        data-testid="inline-client-save-btn"
      >
        {saving ? 'Création...' : 'Créer et sélectionner'}
      </Button>
    </div>
  );
};

const AssignDialog = ({ loan, clients: initialClients, savTickets, onAssign, onClose, api }) => {
  const [clientId, setClientId] = useState('');
  const [savTicketId, setSavTicketId] = useState('');
  const [loanType, setLoanType] = useState('test');
  const [expectedDays, setExpectedDays] = useState(14);
  const [notes, setNotes] = useState('');
  const [showNewClient, setShowNewClient] = useState(false);
  const [clients, setClients] = useState(initialClients);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!clientId) {
      toast.error('Veuillez sélectionner un client');
      return;
    }
    if (loanType === 'sav' && !savTicketId) {
      toast.error('Veuillez sélectionner un ticket SAV');
      return;
    }
    onAssign(loan.id, clientId, loanType === 'sav' ? savTicketId : null, loanType, expectedDays, notes);
    onClose();
  };

  const handleClientCreated = (newClient) => {
    setClients(prev => [...prev, newClient]);
    setClientId(newClient.id);
    setShowNewClient(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label>Type de prêt *</Label>
        <Select value={loanType} onValueChange={setLoanType}>
          <SelectTrigger data-testid="assign-type-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="test">Test client (essai produit)</SelectItem>
            <SelectItem value="demo">Démonstration</SelectItem>
            <SelectItem value="sav">SAV (remplacement)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>Client *</Label>
          {!showNewClient && (
            <button
              type="button"
              onClick={() => setShowNewClient(true)}
              className="text-[#00E01A] text-xs hover:underline flex items-center gap-1"
              data-testid="create-new-client-btn"
            >
              <Plus size={12} />
              Nouveau client
            </button>
          )}
        </div>
        
        {showNewClient ? (
          <InlineClientForm
            api={api}
            onCreated={handleClientCreated}
            onCancel={() => setShowNewClient(false)}
          />
        ) : (
          <Select value={clientId} onValueChange={setClientId}>
            <SelectTrigger data-testid="assign-client-select">
              <SelectValue placeholder="Sélectionner..." />
            </SelectTrigger>
            <SelectContent className="max-h-[200px]">
              {clients.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
      
      {loanType === 'sav' && (
        <div className="space-y-2">
          <Label>Ticket SAV associé *</Label>
          <Select value={savTicketId} onValueChange={setSavTicketId}>
            <SelectTrigger data-testid="assign-sav-select">
              <SelectValue placeholder="Sélectionner..." />
            </SelectTrigger>
            <SelectContent className="max-h-[200px]">
              {savTickets
                .filter(t => t.status !== 'closed')
                .map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.ticket_number} - {t.client_name}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="space-y-2">
        <Label>Durée prévue (jours)</Label>
        <Input
          type="number"
          value={expectedDays}
          onChange={(e) => setExpectedDays(parseInt(e.target.value) || 14)}
          min={1}
          data-testid="assign-days-input"
        />
      </div>

      <div className="space-y-2">
        <Label>Notes (optionnel)</Label>
        <Input
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Ex: Test avant achat RAVIK S"
          data-testid="assign-notes-input"
        />
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t border-[#2C3338]">
        <Button type="button" variant="outline" onClick={onClose}>
          Annuler
        </Button>
        <Button type="submit" className="bg-[#00E01A] hover:bg-[#00B815] text-black">
          Attribuer
        </Button>
      </div>
    </form>
  );
};

const LoanCard = ({ loan, onAssign, onReturn }) => {
  const isOverdue = loan.status === 'on_loan' && 
    loan.expected_return_date && 
    new Date(loan.expected_return_date) < new Date();

  const loanTypeLabels = {
    test: 'Test client',
    demo: 'Démo',
    sav: 'SAV'
  };

  return (
    <div className={`bg-[#1D2327] rounded-xl border transition-all ${
      isOverdue ? 'border-[#E74C3C]' : 'border-[#2C3338] hover:border-[#00E01A]/30'
    }`}>
      <div className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div>
            <p className="text-[#8C8F94] text-xs font-mono">{loan.serial_number}</p>
            <h3 className="text-white font-semibold mt-1">{loan.product_name}</h3>
          </div>
          <span className={`px-2 py-1 rounded-full text-xs font-medium border ${statusColors[loan.status]}`}>
            {statusLabels[loan.status]}
          </span>
        </div>

        {loan.status === 'on_loan' && (
          <div className="bg-[#141617] rounded-lg p-3 mb-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 text-[#8C8F94] text-sm">
                <User size={14} />
                <span>{loan.current_client_name}</span>
              </div>
              {loan.loan_type && (
                <span className="text-xs px-2 py-0.5 rounded bg-[#2C3338] text-white">
                  {loanTypeLabels[loan.loan_type] || loan.loan_type}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Calendar size={14} className={isOverdue ? 'text-[#E74C3C]' : 'text-[#8C8F94]'} />
              <span className={isOverdue ? 'text-[#E74C3C]' : 'text-[#8C8F94]'}>
                Retour prévu: {new Date(loan.expected_return_date).toLocaleDateString('fr-FR')}
              </span>
            </div>
            {loan.loan_notes && (
              <p className="text-[#52555A] text-xs mt-2 italic">"{loan.loan_notes}"</p>
            )}
            {isOverdue && (
              <div className="flex items-center gap-2 text-[#E74C3C] text-xs mt-2">
                <AlertTriangle size={12} />
                <span>Prêt en retard</span>
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-between pt-3 border-t border-[#2C3338]">
          <span className="text-[#8C8F94] text-xs">{loan.franchise_name}</span>
          
          {loan.status === 'available' ? (
            <Button
              size="sm"
              onClick={() => onAssign(loan)}
              className="bg-[#00E01A] hover:bg-[#00B815] text-black text-xs"
              data-testid={`assign-loan-${loan.id}`}
            >
              Attribuer
            </Button>
          ) : loan.status === 'on_loan' ? (
            <Button
              size="sm"
              onClick={() => onReturn(loan.id)}
              className="bg-[#E74C3C] hover:bg-[#C0392B] text-white text-xs"
              data-testid={`finalize-loan-${loan.id}`}
            >
              Finaliser
            </Button>
          ) : null}
        </div>
      </div>

      {loan.loan_history?.length > 0 && (
        <div className="px-4 pb-4">
          <details className="group">
            <summary className="text-[#8C8F94] text-xs cursor-pointer hover:text-white">
              Historique ({loan.loan_history.length} prêts)
            </summary>
            <div className="mt-2 space-y-2 pl-2 border-l border-[#2C3338]">
              {loan.loan_history.slice(0, 3).map((h, idx) => (
                <div key={idx} className="text-xs text-[#52555A]">
                  {new Date(h.start_date).toLocaleDateString('fr-FR')} → {new Date(h.return_date).toLocaleDateString('fr-FR')}
                </div>
              ))}
            </div>
          </details>
        </div>
      )}
    </div>
  );
};

const Loans = () => {
  const { api, isSuperAdmin, isAdminFranchise } = useAuth();
  const [loans, setLoans] = useState([]);
  const [products, setProducts] = useState([]);
  const [clients, setClients] = useState([]);
  const [savTickets, setSavTickets] = useState([]);
  const [franchises, setFranchises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState(null);

  const fetchData = async () => {
    try {
      const [loansRes, productsRes, clientsRes, savRes, franchisesRes] = await Promise.all([
        api.get('/loans'),
        api.get('/products'),
        api.get('/clients'),
        api.get('/sav'),
        api.get('/franchises')
      ]);
      setLoans(loansRes.data);
      setProducts(productsRes.data);
      setClients(clientsRes.data);
      setSavTickets(savRes.data);
      setFranchises(franchisesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [api]);

  const handleSave = async (data) => {
    try {
      await api.post('/loans', data);
      toast.success('Équipement de prêt créé');
      fetchData();
      setDialogOpen(false);
    } catch (error) {
      console.error('Failed to create loan equipment:', error);
      toast.error('Erreur lors de la création');
    }
  };

  const handleAssign = async (loanId, clientId, savTicketId, loanType, expectedDays, notes) => {
    try {
      let url = `/loans/${loanId}/assign?client_id=${clientId}&loan_type=${loanType}&expected_days=${expectedDays}`;
      if (savTicketId) {
        url += `&sav_ticket_id=${savTicketId}`;
      }
      if (notes) {
        url += `&notes=${encodeURIComponent(notes)}`;
      }
      await api.post(url);
      toast.success('Équipement attribué');
      fetchData();
    } catch (error) {
      console.error('Failed to assign loan:', error);
      toast.error('Erreur lors de l\'attribution');
    }
  };

  const handleReturn = async (loanId) => {
    try {
      await api.post(`/loans/${loanId}/return`);
      toast.success('Équipement retourné');
      fetchData();
    } catch (error) {
      console.error('Failed to return loan:', error);
      toast.error('Erreur lors du retour');
    }
  };

  const filteredLoans = loans.filter(l => {
    const matchesSearch = 
      l.serial_number?.toLowerCase().includes(search.toLowerCase()) ||
      l.product_name?.toLowerCase().includes(search.toLowerCase()) ||
      l.current_client_name?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || l.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const availableLoans = filteredLoans.filter(l => l.status === 'available');
  const onLoanLoans = filteredLoans.filter(l => l.status === 'on_loan');
  const overdueLoans = onLoanLoans.filter(l => 
    l.expected_return_date && new Date(l.expected_return_date) < new Date()
  );

  if (loading) {
    return (
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-[#1D2327] rounded-xl border border-[#2C3338] p-4 animate-pulse">
              <div className="h-4 w-24 bg-[#2C3338] rounded mb-2"></div>
              <div className="h-5 w-32 bg-[#2C3338] rounded"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white font-['Outfit']">
            Matériel de prêt
          </h1>
          <p className="text-[#8C8F94] mt-1">
            {loans.length} équipement{loans.length > 1 ? 's' : ''} • {availableLoans.length} disponible{availableLoans.length > 1 ? 's' : ''}
          </p>
        </div>
        
        {(isSuperAdmin() || isAdminFranchise()) && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <button
                className="flex items-center gap-2 bg-[#00E01A] hover:bg-[#00B815] text-black font-semibold px-4 py-2.5 rounded-full transition-all shadow-[0_0_15px_rgba(0,224,26,0.3)]"
                data-testid="add-loan-btn"
              >
                <Plus size={18} />
                <span className="hidden md:inline">Ajouter</span>
              </button>
            </DialogTrigger>
            <DialogContent className="bg-[#1D2327] border-[#2C3338] text-white max-w-md">
              <DialogHeader>
                <DialogTitle className="text-xl font-bold font-['Outfit']">
                  Nouvel équipement de prêt
                </DialogTitle>
              </DialogHeader>
              <LoanForm
                products={products}
                franchises={franchises}
                onSave={handleSave}
                onClose={() => setDialogOpen(false)}
              />
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Alert for overdue loans */}
      {overdueLoans.length > 0 && (
        <div className="bg-[#2A1515] border border-[#E74C3C]/30 rounded-xl p-4 flex items-center gap-3">
          <AlertTriangle size={24} className="text-[#E74C3C]" />
          <div>
            <p className="text-[#E74C3C] font-semibold">
              {overdueLoans.length} prêt{overdueLoans.length > 1 ? 's' : ''} en retard
            </p>
            <p className="text-[#E74C3C]/70 text-sm">
              Des équipements auraient dû être retournés
            </p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8C8F94]" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher..."
            className="w-full bg-[#1D2327] border border-[#2C3338] rounded-lg pl-10 pr-4 py-2.5 text-white placeholder-[#52555A] focus:outline-none focus:border-[#00E01A] transition-colors"
            data-testid="loan-search-input"
          />
        </div>
        
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full md:w-48 bg-[#1D2327] border-[#2C3338]">
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent className="bg-[#1D2327] border-[#2C3338]">
            <SelectItem value="all">Tous statuts</SelectItem>
            <SelectItem value="available">Disponible</SelectItem>
            <SelectItem value="on_loan">En prêt</SelectItem>
            <SelectItem value="in_maintenance">En maintenance</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Loans Grid */}
      {filteredLoans.length === 0 ? (
        <div className="text-center py-12">
          <Truck size={48} className="mx-auto text-[#8C8F94] mb-4" />
          <p className="text-[#8C8F94]">Aucun équipement de prêt trouvé</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredLoans.map((loan) => (
            <LoanCard
              key={loan.id}
              loan={loan}
              onAssign={(l) => {
                setSelectedLoan(l);
                setAssignDialogOpen(true);
              }}
              onReturn={handleReturn}
            />
          ))}
        </div>
      )}

      {/* Assign Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="bg-[#1D2327] border-[#2C3338] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold font-['Outfit']">
              Attribuer l'équipement
            </DialogTitle>
          </DialogHeader>
          {selectedLoan && (
            <AssignDialog
              loan={selectedLoan}
              clients={clients}
              savTickets={savTickets}
              api={api}
              onAssign={handleAssign}
              onClose={() => {
                setAssignDialogOpen(false);
                setSelectedLoan(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Loans;
